import React from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Dayjs } from 'dayjs';
import api from '../api';
import { showMedicalContentSelector } from '../redux/config/selfData';
import { currentNursingHomeSelector } from '../redux/config/currentNursingHome';
import { RootState } from '../redux/core/store';
import { IGetDrinkRes } from '../types/api/care-settings/getDrink';
import { IGetLaxativeRes } from '../types/api/care-settings/getLaxative';
import { IGetDailyCareRecordRes } from '../types/api/traversal/getDailyCareRecord';
import { exportCareRecordCSV } from '../utils/careRecordCsv';

interface ExportArgs {
  data: IGetDailyCareRecordRes[];
  residentFullName?: string;
  start: Dayjs;
  end: Dayjs;
}

const careRecordMasterCache = new Map<
  number,
  {
    drinks: IGetDrinkRes[];
    laxatives: IGetLaxativeRes[];
  }
>();

const fetchCareRecordMasterData = async (nursingHomeId: number) => {
  const cached = careRecordMasterCache.get(nursingHomeId);
  if (cached) {
    return cached;
  }

  const [laxatives, drinks] = await Promise.all([
    api.get('/nursing-homes/:nursingHomeId/laxatives', {
      params: { nursingHomeId, sort: 'desc', limit: 1000 },
    }),
    api.get('/nursing-homes/:nursingHomeId/drinks', {
      params: { nursingHomeId, sort: 'desc', limit: 1000 },
    }),
  ]);

  const masterData = { drinks, laxatives };
  careRecordMasterCache.set(nursingHomeId, masterData);
  return masterData;
};

export const useCareRecordCsvExporter = () => {
  const { nursingHomeId: routeNursingHomeId } = useParams<{ nursingHomeId?: string }>();
  const showMedicalContent = useSelector(showMedicalContentSelector);
  const currentNursingHome = useSelector(currentNursingHomeSelector);
  const selfNursingHomeId = useSelector((state: RootState) => state.selfData.selfUser.nursingHomeId);

  const resolvedNursingHomeId = React.useMemo(() => {
    return Number(routeNursingHomeId) || selfNursingHomeId;
  }, [routeNursingHomeId, selfNursingHomeId]);

  const exportCsv = React.useCallback(
    async ({ data, residentFullName, start, end }: ExportArgs) => {
      const hasNursingHomeId = Number.isFinite(resolvedNursingHomeId) && resolvedNursingHomeId > 0;
      const masterData = hasNursingHomeId
        ? await fetchCareRecordMasterData(resolvedNursingHomeId)
        : { drinks: undefined, laxatives: undefined };

      return exportCareRecordCSV({
        data,
        residentFullName,
        start,
        end,
        showMedicalContent,
        currentNursingHomeName: currentNursingHome.name,
        drinks: masterData.drinks,
        laxatives: masterData.laxatives,
      });
    },
    [currentNursingHome.name, resolvedNursingHomeId, showMedicalContent]
  );

  return { exportCareRecordCsv: exportCsv };
};
